import { supabase, uid, isRecord } from './shared'
import type { RecordLike } from './shared'
import { mapMessageFromSupabase, mapMessageResponseFromSupabase } from '@/api/mappers/messages'
import { attachDirectorNoteToMessage } from './director-notes'
import type { MessageLike } from './director-notes'
import { compareTimelineEntries } from '@/lib/messageSlots'
import type {
  MessageUpdateData,
} from '../../contract'
import type { MessageAlternative } from '@/types/domain'

export function sortMessagesChronologically(messages: MessageLike[] = []) {
  // Canonical order is insertion order (the bigint `messages.id`), not the
  // timestamp — see compareTimelineEntries. This keeps the client's display
  // order aligned with the id-keyed pagination cursor.
  return [...messages].sort(compareTimelineEntries);
}

export async function updateMessage(id: number | string, messageData: MessageUpdateData) {
  const userId = await uid();
  const { director_note, phase, ...messageFields } = messageData;
  void director_note;
  const payload: RecordLike = { ...messageFields };
  if (phase !== undefined) {
    payload.is_streaming = phase === 'thinking' || phase === 'streaming';
    payload.is_complete = phase === 'complete';
  }

  const { data, error } = await supabase
    .from('messages')
    .update(payload)
    .eq('id', id)
    .eq('user_id', userId)
    .select('*')
    .single();

  if (error) throw error;
  return mapMessageResponseFromSupabase({
    message: await attachDirectorNoteToMessage(data, userId),
  });
}

function mapAltRow(row: unknown): MessageAlternative {
  if (!isRecord(row)) return row as MessageAlternative;
  return {
    ...row,
    message_id: Number(row.parent_message_id),
  } as unknown as MessageAlternative;
}

export async function getMessageAlternatives(messageId: number | string) {
  const userId = await uid();

  const { data: anchor, error: anchorError } = await supabase
    .from('messages')
    .select('*')
    .eq('id', messageId)
    .eq('user_id', userId)
    .single();

  if (anchorError) throw anchorError;

  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('user_id', userId)
    .eq('parent_message_id', messageId)
    .order('t', { ascending: true });

  if (error) throw error;
  const alternatives = (data ?? []).map(mapAltRow);
  return {
    alternatives,
    original: anchor ? mapMessageFromSupabase(anchor) : undefined,
    active_variant_id: isRecord(anchor) && anchor.active_variant_id != null ? Number(anchor.active_variant_id) : null,
  };
}
